import { useEffect } from "react";
import { useLocation } from "react-router-dom";

/**
 * PassiveClickBeacon — records a click server-side when a visitor lands on
 * ANY femcare.life page carrying a saved publisher link's tracking params.
 *
 * Design goals (from the owner spec):
 *   - PASSIVE: never modifies existing UTM attribution, Razorpay flow, GA4,
 *     AdSense or user-facing content. The tag is invisible.
 *   - Existing publisher links keep working as-is — no /r/... redirect
 *     wrapper is introduced.
 *   - DEDUPE: at most one click per publisher link per browser session per
 *     30-minute window. Duplicate refreshes DO NOT count. sessionStorage
 *     handles the client side; server-side enforces the same window via a
 *     unique index on sha256(ip|ua|link_key|bucket).
 *   - NO PII: nothing user-identifying is sent. Only the tracking params
 *     already visible in the URL (utm_*, publisher_id, pid, sub_*) and the
 *     pathname / referrer. No email, phone, auth token, user id.
 *
 * Trigger rule:
 *   Only fires when the URL contains at least one of utm_source /
 *   publisher_id / pid / utm_content — matching the identifiers used by
 *   the server-side link matcher (`_click_link_key`). Landings without any
 *   tracking signal are ignored (they can't be attributed to a link).
 */

const BACKEND_URL   = process.env.REACT_APP_BACKEND_URL;
const DEDUPE_MS     = 30 * 60 * 1000;   // 30 minutes — matches server bucket
const STORAGE_PREFIX = "fc_click:";

// The tracking-only param whitelist. If we EVER add more here we must also
// widen `_ClickBody` on the backend — keep in sync.
const TRACKED_KEYS = [
    "utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term",
    "publisher_id", "pid", "sub_id", "sub1", "sub2", "sub3",
];

function readTrackingParams(search) {
    const q = new URLSearchParams(search || "");
    const out = {};
    for (const k of TRACKED_KEYS) {
        const v = q.get(k);
        if (v) out[k] = v;
    }
    return out;
}

function fingerprintForDedupe(params) {
    // Same 5-field fingerprint the backend uses (`_click_link_key`) — anything
    // else in the params (utm_term, sub_*) does not create a distinct link.
    return [
        (params.publisher_id || "").toLowerCase(),
        (params.pid          || "").toLowerCase(),
        (params.utm_source   || "").toLowerCase(),
        (params.utm_campaign || "").toLowerCase(),
        (params.utm_content  || "").toLowerCase(),
    ].join("|");
}

export default function PassiveClickBeacon() {
    const { pathname, search } = useLocation();

    useEffect(() => {
        const params = readTrackingParams(search);
        // No tracking signal at all → this landing cannot be attributed to a
        // saved publisher link, so we ignore it entirely.
        const hasSignal = ["utm_source", "publisher_id", "pid", "utm_content"]
            .some((k) => params[k]);
        if (!hasSignal) return;

        // Client-side dedupe: same (fingerprint) within DEDUPE_MS in this
        // browser session is treated as the same click.
        const fp   = fingerprintForDedupe(params);
        const key  = STORAGE_PREFIX + fp;
        try {
            const prev = Number(window.sessionStorage.getItem(key) || 0);
            if (prev && (Date.now() - prev) < DEDUPE_MS) return;
            window.sessionStorage.setItem(key, String(Date.now()));
        } catch { /* private mode — fall through and let the server dedupe */ }

        // Fire the beacon. Best-effort — we NEVER surface an error to the UI.
        const body = {
            ...params,
            path: pathname || "/",
            referrer: (document.referrer || "").slice(0, 500),
        };
        try {
            const url = `${BACKEND_URL}/api/web/track/click`;
            // sendBeacon is the safest fire-and-forget primitive here — it
            // survives page navigation and never blocks the main thread.
            if (navigator.sendBeacon) {
                const blob = new Blob([JSON.stringify(body)], { type: "application/json" });
                navigator.sendBeacon(url, blob);
            } else {
                fetch(url, {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify(body),
                    keepalive: true,
                    // Explicitly do NOT send credentials — the beacon must
                    // remain anonymous and never accidentally piggyback the
                    // fc_sid session cookie.
                    credentials: "omit",
                }).catch(() => { /* swallow — beacon is best-effort */ });
            }
        } catch { /* swallow — beacon is best-effort */ }
    }, [pathname, search]);

    return null;
}
